import type { PublisherListOut } from '@/api/generated/schemas';

/** Source shape: the fields the tree is built from, so tests stay small. */
export type PublisherTreeSource = Pick<
  PublisherListOut,
  'id' | 'name' | 'parent_id' | 'name_with_role'
>;

/**
 * One request has to cover every publisher a tree offers: a branch that never
 * loaded cannot be checked, and `getMany` otherwise falls back to the server's
 * default of 100.
 */
export const PUBLISHER_TREE_LIMIT = 1000;

export interface PublisherTreeNode<T extends PublisherTreeSource> {
  publisher: T;
  /** Absent, never empty — antd draws a dead expander on an empty array. */
  children?: PublisherTreeNode<T>[];
}

/**
 * Groups a flat publisher list into the imprint hierarchy it describes.
 *
 * Membership is per-publisher everywhere it matters — an organization links
 * each publisher on its own — so an imprint can arrive without its parent.
 * Such a node is promoted to the top rather than dropped: callers render
 * counts taken from the input, and a vanishing row would contradict them.
 */
export const buildPublisherTree = <T extends PublisherTreeSource>(
  publishers: T[]
): PublisherTreeNode<T>[] => {
  const present = new Set(publishers.map((p) => p.id));
  const childrenByParent = new Map<number, T[]>();
  const roots: T[] = [];

  for (const publisher of publishers) {
    const parentId = publisher.parent_id;
    // A parent outside this set cannot be nested under, so the imprint is one.
    if (parentId == null || !present.has(parentId)) {
      roots.push(publisher);
      continue;
    }
    const siblings = childrenByParent.get(parentId);
    if (siblings) siblings.push(publisher);
    else childrenByParent.set(parentId, [publisher]);
  }

  const byName = (a: T, b: T) => a.name.localeCompare(b.name);
  const placed = new Set<number>();

  // `seen` guards against a parent cycle, which the schema does not forbid.
  const toNodes = (level: T[], seen: Set<number>): PublisherTreeNode<T>[] =>
    [...level].sort(byName).flatMap((publisher) => {
      if (seen.has(publisher.id)) return [];
      placed.add(publisher.id);
      const children = toNodes(
        childrenByParent.get(publisher.id) ?? [],
        new Set(seen).add(publisher.id)
      );
      return [{ publisher, ...(children.length > 0 && { children }) }];
    });

  const tree = toNodes(roots, new Set());

  // A cycle leaves its members rootless and so unreachable above. Surface them
  // flat: callers show counts taken from the input, which must still add up.
  const unreachable = publishers
    .filter((p) => !placed.has(p.id))
    .sort(byName)
    .map((publisher) => ({ publisher }));

  return [...tree, ...unreachable];
};

export interface PublisherTreeDataNode {
  value: number;
  title: string;
  children?: PublisherTreeDataNode[];
}

/**
 * Tree nodes as antd `TreeSelect` data. Labels use `name_with_role` — the
 * ancestry string used elsewhere would repeat what the nesting already shows.
 */
export const toPublisherTreeData = <T extends PublisherTreeSource>(
  nodes: PublisherTreeNode<T>[]
): PublisherTreeDataNode[] =>
  nodes.map(({ publisher, children }) => ({
    value: publisher.id,
    title: publisher.name_with_role,
    ...(children && { children: toPublisherTreeData(children) }),
  }));
